import React from 'react';
import { formatINR, numToWords } from './billFormatUtils';

function getLineDiscountDisplay(row) {
  if (!(parseFloat(row.lineDiscountValue) > 0)) return '—';
  if ((row.lineDiscountType || 'amount') === 'percent') {
    return `${row.lineDiscountValue}%`;
  }
  return `₹${formatINR(row.lineDiscountValue)}`;
}

function getRowMeasurement(row = {}) {
  const display = String(row.qtyDisplay || '').trim();
  if (display) {
    const qtyStr = String(row.qty ?? '').trim();
    if (qtyStr && display === qtyStr) return '';
    return display;
  }
  const expr = String(row.qtyExpr || '').trim();
  if (expr) return expr;
  const nos = parseFloat(row.nos);
  const length = parseFloat(row.length);
  const height = parseFloat(row.height);
  const hasDims =
    (Number.isFinite(length) && length > 0) ||
    (Number.isFinite(height) && height > 0) ||
    (Number.isFinite(nos) && nos > 1);
  if (!hasDims) return '';
  const a = Number.isFinite(nos) && nos > 0 ? nos : 1;
  const b = Number.isFinite(length) && length > 0 ? length : 1;
  const c = Number.isFinite(height) && height > 0 ? height : 1;
  return `${a}×${b}×${c}`;
}

export default function QuotationItemsTable({
  variant = 'standard',
  displayItems,
  showAmounts,
  totalBeforeDiscount,
  discount,
  payableAmount,
  hasLineDiscount,
  discountLabel,
  hasSubPartyColumn = false,
}) {
  const showLineDiscountCol = variant === 'colored' && hasLineDiscount;
  const columnCount = 5 + (hasSubPartyColumn ? 1 : 0) + (showLineDiscountCol ? 1 : 0);
  const summaryLabelColSpan = columnCount - 1;
  const summaryFullColSpan = columnCount;

  const tableMinWidth =
    variant === 'colored'
      ? hasSubPartyColumn ? 'sm:min-w-[640px]' : 'sm:min-w-[560px]'
      : hasSubPartyColumn ? 'sm:min-w-[640px]' : 'sm:min-w-[520px]';
  const theadClass =
    variant === 'colored'
      ? 'bg-gradient-to-r from-sky-100/90 to-indigo-100/80 text-gray-800'
      : 'bg-gray-100';

  return (
    <div className="quotation-table-wrap overflow-x-auto mt-4 mb-4 max-w-full">
      <table className={`quotation-items-table w-full min-w-0 ${tableMinWidth} border border-gray-300 text-xs border-collapse`}>
        <thead className={variant === 'standard' ? theadClass : undefined}>
          {variant === 'colored' ? (
            <tr className={theadClass}>
              <th className="border px-2 py-2 text-left align-middle">Sr.</th>
              {hasSubPartyColumn && <th className="border px-2 py-2 text-left align-middle">Sub party</th>}
              <th className="border px-2 py-2 text-left align-middle">Description</th>
              <th className="border px-2 py-2 text-right align-middle">Qty</th>
              <th className="border px-2 py-2 text-right align-middle">Rate</th>
              {showLineDiscountCol && <th className="border px-2 py-2 text-right align-middle">Disc</th>}
              <th className="border px-2 py-2 text-right align-middle">Amount</th>
            </tr>
          ) : (
            <tr>
              <th className="border px-2 py-2 align-middle">Sr.</th>
              {hasSubPartyColumn && <th className="border px-2 py-2 align-middle">Sub party</th>}
              <th className="border px-2 py-2 align-middle">Description</th>
              <th className="border px-2 py-2 align-middle">Qty</th>
              <th className="border px-2 py-2 align-middle">Rate</th>
              <th className="border px-2 py-2 align-middle">Amount</th>
            </tr>
          )}
        </thead>
        <tbody>
          {displayItems.map((row, idx) => {
            const measurement = getRowMeasurement(row);
            return (
            <tr
              key={`${row._billRowIndex ?? idx}-${idx}`}
              className={`quotation-item-row${variant === 'colored' ? (idx % 2 === 0 ? ' bg-gray-50' : ' bg-white') : ''}`}
            >
              <td className="border px-2 py-2 text-center align-middle">{idx + 1}</td>
              {hasSubPartyColumn && (
                <td className="border px-2 py-2 align-middle text-left text-[0.65rem] text-gray-700">
                  {row._subPartyName || '—'}
                </td>
              )}
              <td className="border px-2 py-2 align-middle break-words">
                <div>{row.description || row.itemName || ''}</div>
                {measurement ? (
                  <div className="quotation-item-measurement" style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px' }}>
                    {measurement}
                  </div>
                ) : null}
              </td>
              <td className="border px-2 py-2 text-right align-middle">
                {row.qty || row.nos || ''} {row.unit || row.quantityMeasurement || 'Nos.'}
              </td>
              <td className="border px-2 py-2 text-right align-middle">₹{formatINR(row.rate)}</td>
              {showLineDiscountCol && (
                <td className="border px-2 py-2 text-right align-middle text-orange-600">
                  {getLineDiscountDisplay(row)}
                </td>
              )}
              <td className="border px-2 py-2 text-right align-middle">₹{formatINR(row.amount)}</td>
            </tr>
            );
          })}
          {showAmounts && (
            <>
              <tr className={variant === 'colored' ? 'bg-sky-50/70 quotation-summary-row' : undefined}>
                <td colSpan={summaryLabelColSpan} className="border px-2 py-2 text-right font-semibold align-middle">Total</td>
                <td className={`border px-2 py-2 text-right font-bold align-middle${variant === 'colored' ? ' text-sky-700' : ''}`}>
                  ₹{formatINR(totalBeforeDiscount)}
                </td>
              </tr>
              {discount > 0 && (
                <tr className={variant === 'colored' ? 'bg-orange-50/70 quotation-summary-row' : undefined}>
                  <td colSpan={summaryLabelColSpan} className="border px-2 py-2 text-right align-middle">
                    {variant === 'colored' ? <span className="font-semibold">{discountLabel}</span> : 'Discount'}
                  </td>
                  <td className={`border px-2 py-2 text-right align-middle${variant === 'colored' ? ' font-bold text-orange-600' : ''}`}>
                    -₹{formatINR(discount)}
                  </td>
                </tr>
              )}
              <tr className={variant === 'colored' ? 'bg-emerald-50/70 quotation-summary-row' : undefined}>
                <td colSpan={summaryLabelColSpan} className="border px-2 py-2 text-right font-semibold align-middle">Payable Amount</td>
                <td className={`border px-2 py-2 text-right font-bold align-middle${variant === 'colored' ? ' text-emerald-700' : ''}`}>
                  ₹{formatINR(payableAmount)}
                </td>
              </tr>
              <tr className={variant === 'colored' ? 'bg-gray-50 quotation-summary-row' : undefined}>
                <td
                  colSpan={summaryFullColSpan}
                  className={`border px-2 py-2 text-right italic align-middle${variant === 'colored' ? ' text-gray-600' : ''}`}
                >
                  In Words: {numToWords(payableAmount)}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
